"use client";

import { c as _c } from "react/compiler-runtime";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { isEqual } from "~/shared/utils";
import { usePrev } from "./";

/**
 * Extends `React.useState` to update value if `initialValue` changes after component mounts
 * @function useAsyncState
 * @param {T} initialValue initial state value
 * @param {boolean | undefined} keepSynced if true, only syncs state once on mount
 * @returns {[T, Dispatch<SetStateAction<T>>]} `useState` tuple
 */
export const useAsyncState = (initialValue, t0) => {
  const $ = _c(7);
  const keepSynced = t0 === undefined ? false : t0;
  const [value, setValue] = useState(initialValue);
  const prevInitialValue = usePrev(initialValue);
  const hasSynced = useRef(false);
  let t1;
  let t2;
  if ($[0] !== initialValue || $[1] !== keepSynced || $[2] !== prevInitialValue) {
    t1 = () => {
      if (!hasSynced.current && !isEqual(prevInitialValue, initialValue)) {
        setValue(initialValue);
        if (!keepSynced) {
          hasSynced.current = true;
        }
      }
    };
    t2 = [prevInitialValue, initialValue, keepSynced];
    $[0] = initialValue;
    $[1] = keepSynced;
    $[2] = prevInitialValue;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== value) {
    t3 = [value, setValue];
    $[5] = value;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export default useAsyncState;